/**
 * How an audit event reads on the Activity page (CO-87).
 *
 * The API records stable identifiers (`environment.domain_set`,
 * `project_application.updated`) and a free-form metadata object. People
 * should see "Hostname set" and "Branch: main", not the identifiers, raw
 * UUIDs or `[object Object]`.
 */
import {
  deploymentModeLabel,
  domainPurposeLabel,
  environmentKindLabel,
  humanize,
  labelFor,
  recoveryPointTypeLabel,
  rollbackPolicyLabel,
  strategyLabel,
  valueSourceLabel,
} from '../labels';
import { formatBytes } from './project/projectHealth';

/** Curated names; anything else is humanized from the identifier. */
const actionLabels: Record<string, string> = {
  'project.created': 'Project created',
  'project.deleted': 'Project deleted',
  'environment.created': 'Environment added',
  'environment.domain_set': 'Hostname set',
  'environment.domain_removed': 'Hostname removed',
  'environment_variable.created': 'Variable added',
  'environment_variable.updated': 'Variable changed',
  'environment_variable.removed': 'Variable removed',
  'frontend_env_var.set': 'Frontend variable set',
  'frontend.magento_binding_set': 'Frontend bound to a Magento website',
  'component.added': 'Component added',
  'component.updated': 'Component settings changed',
  'component.removed': 'Component removed',
  'component.cache_purged': 'Cache invalidated',
  'component.cache_purge_failed': 'Cache invalidation failed',
  'component.cloudflare_token_verified': 'Cloudflare token checked',
  'component.cache_safety_checked': 'Cached routes checked',
  'project_application.created': 'Component added',
  'project_application.updated': 'Component settings changed',
  'placement.set': 'Component placed on a server',
  'binding.set': 'Resource bound',
  'resource.registered': 'Resource registered',
  'magento.topology_discovered': 'Magento websites read',
  'magento.topology_imported': 'Magento websites imported',
  'magento_runtime_config.updated': 'Magento runtime settings changed',
  'onboarding_plan.approved': 'Setup plan approved',
  'deployment.created': 'Deployment created',
  'deployment.started': 'Deployment started',
  'deployment.failed': 'Deployment failed',
  'deployment_group.created': 'Coordinated deployment created',
  'deployment_group.started': 'Coordinated deployment started',
  'execution.created': 'Run created',
  'execution.started': 'Run started',
  'execution.completed': 'Run completed',
  'execution.failed': 'Run failed',
  'execution.cancelled': 'Run cancelled',
  'server.registered': 'Server added',
  'server.removed': 'Server removed',
  'server.connected': 'Agent connected',
  'server.disconnected': 'Agent disconnected',
  'server.capabilities.updated': 'Server check updated',
  'server_connection.created': 'SSH connection added',
  'server_connection.verified': 'SSH connection verified',
  'server_connection.rotated': 'SSH credential replaced',
  'server_connection.deleted': 'SSH connection removed',
  'git_connection.created': 'Repository access added',
  'git_connection.verified': 'Repository access verified',
  'git_connection.rotated': 'Repository token replaced',
  'git_connection.deleted': 'Repository access removed',
  'github_app.installed': 'GitHub App installed',
  'github_app.deleted': 'GitHub App removed',
  'secret.created': 'Secret stored',
  'secret.rotated': 'Secret replaced',
  'secret.deleted': 'Secret removed',
  'backup.policy_changed': 'Backup schedule changed',
  'backup.started': 'Backup started',
  'backup.completed': 'Backup completed',
  'backup.failed': 'Backup failed',
  'restore.started': 'Restore started',
  'restore.completed': 'Restore completed',
  'restore.failed': 'Restore failed',
  'migration.approved': 'Store import approved',
  'migration.imported': 'Store imported',
  'migration.failed': 'Store import failed',
  'incident.resolved': 'Incident resolved',
  'alert.resolved': 'Alert resolved',
  'erp.connection.created': 'ERP connected',
  'erp.connection.updated': 'ERP connection changed',
  'erp.connection.deleted': 'ERP disconnected',
  'store.installation.created': 'Store connected',
  'store.installation.deleted': 'Store disconnected',
  'distribution.source.enabled': 'Product distribution set up',
  'distribution.channel.created': 'Channel connected',
  'distribution.channel.updated': 'Channel settings changed',
  'distribution.channel.deleted': 'Channel removed',
  'wordpress.patch_run.started': 'Update run started',
  'wordpress.maintenance.disabled': 'Maintenance mode turned off',
  'member.invited': 'Teammate invited',
  'member.removed': 'Teammate removed',
  'role.created': 'Role created',
  'role.updated': 'Role changed',
  'role.deleted': 'Role deleted',
  'role.default_changed': 'Default role changed',
  'member.role_changed': 'Teammate’s role changed',
  'invitation.revoked': 'Invitation withdrawn',
  'user.password_changed': 'Password changed',
  'recovery_point.deleted': 'Recovery point deleted',
  'recovery_point.pinned': 'Recovery point kept',
  'recovery_point.unpinned': 'Recovery point no longer kept',
  'restore.test_started': 'Test restore started',
  'backup.retention_changed': 'Backup retention changed',
  'execution.paused': 'Run paused',
  'execution.resumed': 'Run resumed',
  'execution.step_retried': 'Step run again',
  'execution.step_resolved': 'Step outcome settled',
  'deployment.rollback_started': 'Rollback started',
  'terminal.opened': 'Server terminal opened',
  'terminal.closed': 'Server terminal closed',
  'magento.cache_flushed': 'Magento cache flushed',
  'magento.reindex_run': 'Magento reindex run',
  'magento.cron_run': 'Magento cron run',
  'magento.cdn_purged': 'Varnish URL purged',
  'environment.certificate_renewal_requested': 'Certificate renewal requested',
  'incident.acknowledged': 'Incident acknowledged',
  'remediation.approved': 'Fix approved',
  'remediation.executed': 'Fix run',
  'remediation.cancelled': 'Fix cancelled',
  'circuit_breaker.reset': 'Fixes on a target re-enabled',
  'agent.other_host_refused': 'Agent from another machine refused',
  'backup.scheduled_completed': 'Scheduled backup completed',
  'erp.connection.credential_rotated': 'ERP key replaced',
  'erp.issue.ignored': 'ERP issue ignored',
  'erp.issue.resolved': 'ERP issue resolved',
  'erp.issue.accepted': 'ERP issue accepted',
  'environment.runtime_strategy_changed': 'Runtime settings changed',
  'environment.rollback_policy_changed': 'Rollback policy changed',
  'monitoring.policy_changed': 'Alert thresholds changed',
};

/** Suffix of the record of a refused or failed attempt (RES-046). */
const attemptFailed = '.attempt_failed';

export function auditActionLabel(action: string): string {
  if (action.endsWith(attemptFailed)) return `${auditActionLabel(action.slice(0, -attemptFailed.length))} — not done`;
  if (actionLabels[action]) return actionLabels[action]!;
  // `a_b.c_d` → "A b c d": every part humanized, no dots or underscores.
  return words(action.split('.').join(' '));
}

/** The area an action belongs to — what the Activity filters offer. */
export type AuditArea = 'projects' | 'deployments' | 'servers' | 'access' | 'recovery' | 'integrations' | 'team' | 'other';

export const auditAreaLabel: Record<AuditArea, string> = {
  projects: 'Projects and environments',
  deployments: 'Deployments and runs',
  servers: 'Servers',
  access: 'Repositories and secrets',
  recovery: 'Backups and restores',
  integrations: 'ERP and distribution',
  team: 'Team',
  other: 'Other',
};

const areaPrefixes: [string, AuditArea][] = [
  ['deployment', 'deployments'],
  ['execution', 'deployments'],
  ['onboarding_plan', 'deployments'],
  ['incident', 'deployments'],
  ['remediation', 'deployments'],
  ['circuit_breaker', 'deployments'],
  ['monitor', 'deployments'],
  ['terminal', 'servers'],
  ['agent', 'servers'],
  ['managed_server', 'servers'],
  ['recovery_point', 'recovery'],
  ['user', 'team'],
  ['alert', 'deployments'],
  ['wordpress', 'deployments'],
  ['server', 'servers'],
  ['git_connection', 'access'],
  ['github_app', 'access'],
  ['secret', 'access'],
  ['backup', 'recovery'],
  ['restore', 'recovery'],
  ['migration', 'recovery'],
  ['database_backup', 'recovery'],
  ['media_backup', 'recovery'],
  ['erp', 'integrations'],
  ['store', 'integrations'],
  ['distribution', 'integrations'],
  ['member', 'team'],
  ['role', 'team'],
  ['invitation', 'team'],
  ['organization', 'team'],
  ['project', 'projects'],
  ['environment', 'projects'],
  ['component', 'projects'],
  ['frontend', 'projects'],
  ['placement', 'projects'],
  ['binding', 'projects'],
  ['resource', 'projects'],
  ['magento', 'projects'],
];

export function auditArea(action: string): AuditArea {
  const head = action.split('.')[0] ?? '';
  for (const [prefix, area] of areaPrefixes) {
    if (head === prefix || head.startsWith(`${prefix}_`)) return area;
  }
  return 'other';
}

export interface AuditDetail {
  key: string;
  label: string;
  /** Display text; ids are shortened, objects and lists spelled out. */
  text: string;
  /** The full value when text is shortened (for a tooltip). */
  full?: string;
  /** Where the value leads, when it names something the app can show. */
  href?: string;
}

const idLinks: Record<string, (id: string) => string> = {
  project_id: (id) => `/app/projects/${id}`,
  server_id: (id) => `/app/servers/${id}`,
};

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Values that are identifiers with a curated label, by metadata key. */
const valueLabels: Record<string, (value: string) => string> = {
  deployment_mode: (v) => labelFor(deploymentModeLabel, v),
  kind: (v) => labelFor(environmentKindLabel, v),
  deployment_strategy: (v) => labelFor(strategyLabel as Record<string, string>, v),
  rollback_policy: (v) => labelFor(rollbackPolicyLabel as Record<string, string>, v),
  purpose: (v) => labelFor(domainPurposeLabel, v),
  source: (v) => (v.includes('.') ? valueSourceLabel(v) : humanize(v)),
};

/** Words that are acronyms keep their capitals after humanizing. */
const acronyms: Record<string, string> = { tls: 'TLS', dns: 'DNS', url: 'URL', urls: 'URLs', api: 'API', utc: 'UTC', ssh: 'SSH', php: 'PHP', erp: 'ERP', sku: 'SKU', http: 'HTTP', https: 'HTTPS', id: 'ID', ip: 'IP', graphql: 'GraphQL', wordpress: 'WordPress', woocommerce: 'WooCommerce' };

function words(value: string): string {
  return humanize(value)
    .split(' ')
    .map((w) => acronyms[w.toLowerCase()] ?? w)
    .join(' ');
}

/** snake_case identifiers read as words; slugs, hostnames and paths stay. */
const identifier = /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/;

function valueText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  if (typeof value === 'string') {
    if (uuid.test(value)) return value.slice(0, 8);
    return identifier.test(value) ? words(value) : value;
  }
  if (Array.isArray(value)) return value.length === 0 ? 'None' : value.map(valueText).join(', ');
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return 'None';
    // A "changed" object lists what changed; nested values stay short.
    return entries.map(([k, v]) => (v !== null && typeof v === 'object' ? words(k) : `${words(k)}: ${valueText(v)}`)).join(', ');
  }
  return String(value);
}

/** Metadata keys whose name alone would not say what they are. */
const keyLabels: Record<string, string> = {
  drain_bound_seconds: 'Drain timeout',
  drain_policy: 'When the drain timeout is reached',
  asset_retention_hours: 'Asset retention',
  allow_declared_compatible: 'Database-compatible releases',
  read_only_code: 'Read-only Magento code',
  non_root_storefronts: 'Non-root storefronts',
  catalog_search_policy: 'Failed catalog search',
  size_bytes: 'Size',
  off_host: 'Off-host copy',
};

/** Label words for metadata keys: `environment_id` → "Environment". */
function keyLabel(key: string): string {
  return keyLabels[key] ?? words(key.replace(/_id$/, '').replace(/_ids$/, 's'));
}

const isoTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** A moment as people read it: "8 Oct 2026, 00:20". */
export function auditDateTime(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Sizes, durations and moments in the units people use (CO-161). */
function measuredText(key: string, value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (key.endsWith('_bytes')) return formatBytes(value) === '—' ? '0 B' : formatBytes(value);
    if (key.endsWith('_seconds')) return `${value} s`;
    if (key.endsWith('_hours')) return `${value} h`;
  }
  if (typeof value === 'string' && key.endsWith('_at') && isoTime.test(value)) return auditDateTime(value);
  return null;
}

/**
 * Metadata only someone tracing a request needs: the route, its HTTP status
 * and outcome, versions and identifiers. Activity shows it on request.
 */
const technicalKeys = new Set(['route', 'status', 'outcome', 'method', 'request_id', 'correlation_id', 'version', 'allow_declared_compatible_changed', 'hardening_changed']);

export function isTechnicalDetail(key: string, value: unknown): boolean {
  if (technicalKeys.has(key) || key.endsWith('_id') || key.endsWith('_ids')) return true;
  return typeof value === 'string' && uuid.test(value);
}

const runtimeStrategyLabel: Record<string, string> = { blue_green: 'Blue-green runtimes', recreate: 'Recreate strategy' };

/**
 * One readable line for the events people meet most, and the metadata keys
 * it already says (so the facts under it do not repeat them).
 */
export function auditSummary(action: string, metadata: Record<string, unknown> | null | undefined): { text: string; keys: string[] } | null {
  const m = metadata ?? {};
  const str = (key: string) => (typeof m[key] === 'string' ? (m[key] as string) : '');
  switch (action) {
    case 'recovery_point.deleted': {
      // "Backup taken 8 Oct 2026, 06:03 · Scheduled"
      const taken = str('created_at') && isoTime.test(str('created_at')) ? `Backup taken ${auditDateTime(str('created_at'))}` : 'Backup';
      const type = str('type') ? labelFor(recoveryPointTypeLabel, str('type')) : '';
      return { text: [taken, type].filter(Boolean).join(' · '), keys: ['type', 'created_at'] };
    }
    case 'backup.scheduled_completed':
    case 'backup.completed':
      return typeof m.size_bytes === 'number' ? { text: measuredText('size_bytes', m.size_bytes)!, keys: ['size_bytes'] } : null;
    case 'erp.connection.credential_rotated':
      return str('provider') ? { text: `${str('provider').toUpperCase()} issued a new key; Commerce Ops stored it`, keys: ['provider'] } : null;
    case 'environment.runtime_strategy_changed': {
      const to = labelFor(runtimeStrategyLabel, str('to'));
      return { text: str('from') && str('from') !== str('to') ? `${labelFor(runtimeStrategyLabel, str('from'))} → ${to}` : to, keys: ['from', 'to'] };
    }
    case 'environment.rollback_policy_changed': {
      const to = labelFor(rollbackPolicyLabel as Record<string, string>, str('to'));
      return { text: str('from') ? `${labelFor(rollbackPolicyLabel as Record<string, string>, str('from'))} → ${to}` : to, keys: ['from', 'to'] };
    }
    default:
      if (action.startsWith('erp.issue.') && str('code')) {
        return { text: `${words(str('code'))}${str('subject') ? `: ${str('subject')}` : ''}`, keys: ['code', 'subject'] };
      }
      return null;
  }
}

/** The metadata of one event as labelled facts, in a stable order. */
export function auditDetails(metadata: Record<string, unknown> | null | undefined): AuditDetail[] {
  if (!metadata) return [];
  return Object.entries(metadata)
    .filter(([key, value]) => key !== 'actor' && value !== undefined)
    .map(([key, value]) => {
      const text = typeof value === 'string' && valueLabels[key] ? valueLabels[key]!(value) : measuredText(key, value) ?? valueText(value);
      const isId = typeof value === 'string' && uuid.test(value);
      return {
        key,
        label: keyLabel(key),
        text,
        full: isId ? (value as string) : undefined,
        href: isId && idLinks[key] ? idLinks[key]!(value as string) : undefined,
      };
    });
}
